import { Delete } from "@mui/icons-material";
import {
  MenuItem,
  Card,
  CardHeader,
  IconButton,
  CardContent,
} from "@mui/material";
import { Field } from "formik";
import { ReactElement } from "react";
import { Control } from "react-hook-form";

import CsvPreview from "~frontend/components/CsvPreview";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import {
  CSVMetadataValid,
  CSVMetadata,
  matchColumnName,
  PHONE_NUMBER_DEFAULT_COLUMN,
} from "~frontend/util/csv";

import { RhfSelect } from "../react-hook-form-components";

const OTHER_PHONE_NUMBER_COLUMNS = [
  "cell",
  "phonenumber",
  "phone",
  "contactphonenumber",
];

export type ColumnPickerField = {
  name: string;
  label: string;
  autoMatchColumns: string[];
};

export const getDefaultPhoneNumberColumn = (csvMetadata: {
  columns: string[];
}): string | undefined => {
  if (csvMetadata.columns.length === 1) {
    return csvMetadata.columns[0];
  }
  return matchColumnName(csvMetadata, [
    PHONE_NUMBER_DEFAULT_COLUMN,
    ...OTHER_PHONE_NUMBER_COLUMNS,
  ]);
};

export const getDefaultColumnPickerFieldColumn = (
  field: ColumnPickerField,
  csvMetadata: {
    columns: string[];
  }
): string | undefined => {
  if (csvMetadata.columns.length < 2) {
    return undefined;
  }

  return matchColumnName(csvMetadata, field.autoMatchColumns);
};

function PhoneNumberColumnField({
  csvMetadata,
  name = "phoneNumberColumn",
  hidePhoneNumberField = false,
  onClick,
  rhfControl,
}: {
  csvMetadata: CSVMetadataValid;
  name?: string;
  hidePhoneNumberField?: boolean;
  onClick?: () => Promise<boolean>;
  rhfControl?: Control<any>;
}): ReactElement {
  if (hidePhoneNumberField) {
    if (rhfControl) {
      return (
        <RhfSelect
          control={rhfControl}
          name={name}
          sx={{ display: "none" }}
          defaultValue={getDefaultPhoneNumberColumn(csvMetadata)}
        />
      );
    }
    // We only hide the phone number field if there is only one column
    return <Field type="hidden" name={name} />;
  }

  const onClickFn = onClick ? { onClick: () => onClick() } : {};
  const Select = rhfControl ? RhfSelect : FormikSelect;

  return (
    <Select
      control={rhfControl}
      name={name}
      label="Phone Number Column"
      fullWidth
      {...onClickFn}
    >
      {csvMetadata.columns.map(col => (
        <MenuItem key={col} value={col}>
          {col}
        </MenuItem>
      ))}
    </Select>
  );
}

export function ParsedFile({
  fileName,
  metadata,
  name = "phoneNumberColumn",
  hidePhoneNumberField = false,
  onClearFile,
  columnPickerFields = [],
  rhfControl,
}: {
  fileName: string;
  metadata: CSVMetadata;
  name?: string;
  hidePhoneNumberField?: boolean;
  onClearFile: () => void;
  includeVanIdColumn?: boolean;
  columnPickerFields?: ColumnPickerField[];
  rhfControl?: Control<any>;
}): ReactElement {
  return (
    <>
      <Card sx={{ marginBottom: 1, maxWidth: "1200px" }}>
        <CardHeader
          action={
            <IconButton onClick={onClearFile}>
              <Delete />
            </IconButton>
          }
          title={fileName}
          sx={{ textOverflow: "ellipsis" }}
        />
        <CardContent sx={{ overflowX: "scroll", maxWidth: "100%" }}>
          {metadata.valid ? (
            <CsvPreview csvMetadata={metadata} />
          ) : (
            <>
              <p>
                <strong>Invalid CSV</strong>
              </p>
              <ul>
                {metadata.errors.map(e => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </>
          )}
        </CardContent>
      </Card>
      {metadata.valid ? (
        <>
          <PhoneNumberColumnField
            csvMetadata={metadata}
            name={name}
            hidePhoneNumberField={hidePhoneNumberField}
            rhfControl={rhfControl}
          />
          {columnPickerFields.map(field => {
            const Select = rhfControl ? RhfSelect : FormikSelect;
            return (
              <Select
                control={rhfControl}
                name={field.name}
                label={field.label}
                fullWidth
                key={field.name}
                withNoneOption
              >
                {metadata.columns.map(col => (
                  <MenuItem key={col} value={col}>
                    {col}
                  </MenuItem>
                ))}
              </Select>
            );
          })}
        </>
      ) : null}
    </>
  );
}
